import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { getCountryLabel, getCountryOptions } from "@/lib/countryOptions";

const CountrySearchSelect = ({ value, onChange, id, disabled = false }) => {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const countries = useMemo(() => getCountryOptions(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (entry) =>
        entry.code.toLowerCase().includes(q) ||
        entry.name.toLowerCase().includes(q),
    );
  }, [countries, query]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-left text-[#F3F4F6] focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-60"
      >
        <span className="truncate">{getCountryLabel(value) || "Select country"}</span>
        <ChevronsUpDown className="w-4 h-4 shrink-0 text-[#9CA3AF]" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border border-white/10 bg-[#141820] shadow-xl overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code..."
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50"
            />
          </div>
          <ul
            role="listbox"
            className="max-h-60 overflow-y-auto py-1"
            aria-label="Countries"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-[#9CA3AF]">No countries found.</li>
            ) : (
              filtered.map((entry) => {
                const selected = entry.code === value;
                return (
                  <li key={entry.code} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      onClick={() => handleSelect(entry.code)}
                      className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                        selected
                          ? "bg-[#00D1FF]/10 text-[#F3F4F6]"
                          : "text-[#E5E7EB] hover:bg-white/5"
                      }`}
                    >
                      <span className="truncate">
                        {entry.name} ({entry.code})
                      </span>
                      {selected ? <Check className="w-4 h-4 shrink-0 text-[#00D1FF]" /> : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountrySearchSelect;
